import { Icon } from '@eventbrite/eds-icon';
import {
    ChevronDownChunky,
    ChevronUpChunky,
    InfoChunky,
} from '@eventbrite/eds-iconography';
import {
    PaneLayoutInteractionType,
    PANE_LAYOUT_INTERACTION_TYPES,
} from '@eventbrite/eds-modal';
import { formatMajorMoney } from '@eventbrite/intl';
import classNames from 'classnames';
import React, { FunctionComponent } from 'react';
import { ORDER_SUMMARY_BUTTON_LABEL } from '../../constants';

export interface CartSummaryProps {
    currency: string;
    currencyFormat: string;
    isPaneOpen: boolean;
    paneInteraction: typeof PANE_LAYOUT_INTERACTION_TYPES[number];
    onClick: () => void;
    ticketQuantity: number;
    totalCost: number;
}

export const CartSummary: FunctionComponent<CartSummaryProps> = ({
    currency,
    currencyFormat,
    isPaneOpen,
    paneInteraction,
    onClick,
    ticketQuantity,
    totalCost,
}) => {
    const openIndicatorIconForModalMode = <InfoChunky />;

    const openIndicatorIconForCollapsibleMode = isPaneOpen ? (
        <ChevronUpChunky />
    ) : (
        <ChevronDownChunky />
    );

    const openIndicatorIcon =
        paneInteraction === PaneLayoutInteractionType.asModal
            ? openIndicatorIconForModalMode
            : openIndicatorIconForCollapsibleMode;

    const { openIndicatorNode, handleClick, disabled } = ticketQuantity
        ? {
              openIndicatorNode: (
                  <Icon
                      type={openIndicatorIcon}
                      data-testid="cart-summary-icon"
                  />
              ),
              handleClick: onClick,
              disabled: false,
          }
        : {
              openIndicatorNode: null,
              handleClick: undefined,
              disabled: true,
          };

    const formattedTotalCost = formatMajorMoney(
        totalCost,
        currency,
        currencyFormat,
    );

    const ariaPressed =
        paneInteraction === PaneLayoutInteractionType.asModal
            ? undefined
            : isPaneOpen;

    const ariaExpanded =
        paneInteraction === PaneLayoutInteractionType.asModal
            ? undefined
            : isPaneOpen;

    const cartSummaryTextClassName = classNames('eds-text--truncate', {
        'eds-text--right eds-l-pad-vert-2 eds-l-pad-right-4':
            paneInteraction !== PaneLayoutInteractionType.asModal,
        'eds-l-pad-bot-8':
            paneInteraction === PaneLayoutInteractionType.asModal,
    });

    const cartSummaryInlineTextClassName = classNames(
        'eds-text-bm--fixed',
        'eds-text-color--grey-800',
        'eds-text-weight--heavy',
        {
            'eds-align--right':
                paneInteraction === PaneLayoutInteractionType.asModal,
        },
    );

    return (
        <div className="eds-align--right">
            <button
                data-testid="cart-summary-button"
                className="eds-show-down-sw eds-btn--none"
                onClick={handleClick}
                aria-label={ORDER_SUMMARY_BUTTON_LABEL.toString()}
                aria-pressed={ariaPressed}
                aria-expanded={ariaExpanded}
                aria-controls="order-summary"
                disabled={disabled}
            >
                <div className="cart-summary" aria-hidden={true}>
                    <div className={cartSummaryTextClassName}>
                        <span
                            className={cartSummaryInlineTextClassName}
                            data-automation="modal-footer-ticket-price"
                            data-testid="modal-footer-ticket-price"
                        >
                            {openIndicatorNode}
                            {formattedTotalCost}
                        </span>
                    </div>
                </div>
            </button>
        </div>
    );
};
